/**
 * useDeviceProfileNetwork
 *
 * Resolves the network technology a device operates on. Network type lives on the
 * device's Device Profile (a device inherits it), so this reads it from an
 * embedded `deviceProfile` object on the device-by-number response, or fetches
 * the profile by `deviceProfileNumber` when only the id is present.
 *
 * The device↔profile link is still a backend hand-off (spec Feature 6), so until
 * it exists this returns `undefined` and callers fall back to the LoRa credential
 * set (the historical default) via `credentialGroupForNetwork`.
 */

import { useApiQuery } from '@/api/ApiQueryServiceProvider'

/** Which credential field set a network technology maps to. */
export type CredentialGroup = 'lora' | 'cellular' | 'wifi'

/**
 * Maps a Device Profile network type to its credential group. WiFi → SSID/password;
 * 3G/4G/5G → SIM card; everything else (LoRa, BLE, Zigbee, unknown/unresolved) →
 * the LoRa device-key set, which is also the safe historical default.
 */
export function credentialGroupForNetwork(networkType?: string | null): CredentialGroup {
  const n = (networkType ?? '').toLowerCase()
  if (n === 'wifi') return 'wifi'
  if (n === '3g' || n === '4g' || n === '5g') return 'cellular'
  return 'lora'
}

interface ProfileShape {
  deviceProfileNumber?: number
  deviceProfileNetwork?: string
  provisioningRequired?: boolean
}

export function useDeviceProfileNetwork(deviceData: unknown): {
  networkType: string | undefined
  credentialGroup: CredentialGroup
  /**
   * Whether the device's profile requires a provisioning action (Feature 5/7).
   * `undefined` when unresolved (backend field/link pending) — callers should
   * treat undefined as "required" so current behavior is preserved.
   */
  provisioningRequired: boolean | undefined
} {
  // The device-by-number shape varies (and the profile link is a pending backend
  // change), so read defensively.
  const d = (deviceData ?? {}) as {
    device?: { deviceProfileNumber?: number; deviceProfile?: ProfileShape }
    deviceProfile?: ProfileShape
    procurementInfo?: { deviceProfileNumber?: number }
  }
  const embedded = d.deviceProfile ?? d.device?.deviceProfile ?? undefined
  const deviceProfileNumber: number | undefined =
    d.device?.deviceProfileNumber ??
    d.procurementInfo?.deviceProfileNumber ??
    embedded?.deviceProfileNumber

  const { data } = useApiQuery<{ content?: { deviceProfile?: ProfileShape } }>(
    'getDeviceProfile',
    { deviceProfileNumber: deviceProfileNumber ?? 0 },
    { enabled: !!deviceProfileNumber && !embedded?.deviceProfileNetwork }
  )

  const profile = embedded?.deviceProfileNetwork ? embedded : (data?.content?.deviceProfile ?? embedded)
  const networkType = profile?.deviceProfileNetwork ?? undefined
  const provisioningRequired = profile?.provisioningRequired ?? undefined

  return { networkType, credentialGroup: credentialGroupForNetwork(networkType), provisioningRequired }
}
